import { useCallback, useEffect, useRef, useState } from 'react';
import type { AlertEvidenceView } from '../types/alert';
import { getAlertEvidence } from '../lib/api/endpoints';
import { alertEvidenceViewFromDto } from '../lib/api/mappers';
import { ApiError } from '../lib/api/client';

// `GET /alerts/{alertId}/evidence` (BE-52). Se pide bajo demanda, solo cuando
// una tarjeta/modal de alerta se abre, nunca por cada fila de una lista.
//
// `error` conserva el `ApiError` (no lo aplasta a "sin evidencia"): un `403`
// (rol sin permiso), un `501` (el core no tiene archivo de video en este
// entorno) y un fallo de red son cosas distintas y el usuario debe poder
// distinguirlas de "esta alerta no tiene video".
export function useAlertEvidence(alertId: string | null) {
  const [evidence, setEvidence] = useState<AlertEvidenceView | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const cancelledRef = useRef(false);

  const load = useCallback(async () => {
    if (!alertId) {
      setEvidence(null);
      return;
    }
    setIsLoading(true);
    try {
      const res = await getAlertEvidence(alertId);
      if (cancelledRef.current) return;
      setEvidence(alertEvidenceViewFromDto(res));
      setError(null);
    } catch (err) {
      if (!cancelledRef.current) {
        setEvidence(null);
        // Sin sesión real (loginDev, FE-01) el cliente lanza antes de llegar
        // a la red: se muestra como un error de carga, no una pantalla rota.
        setError(
          err instanceof ApiError || err instanceof Error
            ? err
            : new Error('Error desconocido cargando evidencia de la alerta'),
        );
      }
    } finally {
      if (!cancelledRef.current) setIsLoading(false);
    }
  }, [alertId]);

  useEffect(() => {
    cancelledRef.current = false;
    load();
    return () => {
      cancelledRef.current = true;
    };
  }, [load]);

  return { evidence, items: evidence?.items ?? [], isLoading, error, reload: load };
}
